import { css } from '@styled-system/css';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Clock, History, Sparkles, Tag } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import amplitude from '@/amplitude';
import type { ProductSort } from '@/shared/api/types/generated';
import { MultiSelect, Select, type SelectOption } from '@/shared/components/ui/Select';

import type { SellHistoryFilters as Filters } from '../../../features/analysisPageSellsHist/types';

// L'état des articles n'est plus filtrable ici : il appartient au segment
// global (barre de segment), qui recalcule les chiffres de toute la page.
interface SellHistoryFiltersProps {
  filters: Filters;
  availableBrands: Array<{ brand: string; count: number }>;
  onBrandsChange: (brands?: string[]) => void;
  onSortChange: (sort: ProductSort) => void;
  /** Analyse par similarité (image/description) : propose le tri Pertinence. */
  showSimilaritySort?: boolean;
  /** Tri affiché quand filters.sort est absent (défaut serveur). */
  defaultSort?: ProductSort;
}

export const SellHistoryFilters: FC<SellHistoryFiltersProps> = ({
  filters,
  availableBrands,
  onBrandsChange,
  onSortChange,
  showSimilaritySort = false,
  defaultSort = 'newest',
}) => {
  const { t } = useTranslation();

  const filtersContainerStyles = css({
    display: 'flex',
    gap: 'sm',
    alignItems: 'center',
    w: 'fit-content',
  });

  // Marques triées par nombre de produits décroissant (fait par le hook).
  // Multi-sélection : vide = toutes les marques.
  const brandOptions = useMemo<SelectOption[]>(
    () =>
      availableBrands.map((brand) => ({
        value: brand.brand,
        label: `${brand.brand} (${brand.count})`,
      })),
    [availableBrands],
  );

  const sortOptions = useMemo<SelectOption[]>(
    () => [
      // Tri serveur par proximité — uniquement sur les analyses par similarité
      ...(showSimilaritySort
        ? [{ value: 'similarity', label: t('salesFeed.sortSimilarity'), icon: <Sparkles size={13} strokeWidth={1.75} /> }]
        : []),
      { value: 'newest', label: t('salesFeed.sortNewest'), icon: <Clock size={13} strokeWidth={1.75} /> },
      { value: 'oldest', label: t('salesFeed.sortOldest'), icon: <History size={13} strokeWidth={1.75} /> },
      { value: 'priceDesc', label: t('salesFeed.sortPriceDesc'), icon: <ArrowDownWideNarrow size={13} strokeWidth={1.75} /> },
      { value: 'priceAsc', label: t('salesFeed.sortPriceAsc'), icon: <ArrowUpNarrowWide size={13} strokeWidth={1.75} /> },
    ],
    [t, showSimilaritySort],
  );

  const handleBrandsChange = (value: string[]) => {
    // Tracker l'événement avec Amplitude
    amplitude.track('TEST Historique Filter Marques', { brands: value });

    onBrandsChange(value.length ? value : undefined);
  };

  const handleSortChange = (value: string) => {
    // Tracker l'événement avec Amplitude
    amplitude.track('TEST Historique Filter Sort', { sort: value });

    onSortChange(value as ProductSort);
  };

  return (
    <div className={filtersContainerStyles}>
      <MultiSelect
        searchable
        options={brandOptions}
        value={filters.brands ?? []}
        onChange={handleBrandsChange}
        placeholder={t('salesFeed.allBrands')}
        leading={<Tag size={13} strokeWidth={1.75} />}
        width="210px"
        aria-label={t('salesFeed.allBrands')}
      />

      <Select
        options={sortOptions}
        value={filters.sort || defaultSort}
        onChange={handleSortChange}
        placeholder={t('salesFeed.sortPlaceholder')}
        width="190px"
        aria-label={t('salesFeed.sortPlaceholder')}
      />
    </div>
  );
};
